<script setup>
import { computed, nextTick, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ScheduledMessagesAPI from 'dashboard/api/scheduledMessages';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
  initialContent: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const dialogRef = ref(null);
const schedules = ref([]);
const isFetching = ref(false);
const isSaving = ref(false);
const pendingActionId = ref(null);

const weekDays = [
  { value: 1, label: t('SCHEDULED_MESSAGES.WEEKDAYS.MONDAY') },
  { value: 2, label: t('SCHEDULED_MESSAGES.WEEKDAYS.TUESDAY') },
  { value: 3, label: t('SCHEDULED_MESSAGES.WEEKDAYS.WEDNESDAY') },
  { value: 4, label: t('SCHEDULED_MESSAGES.WEEKDAYS.THURSDAY') },
  { value: 5, label: t('SCHEDULED_MESSAGES.WEEKDAYS.FRIDAY') },
  { value: 6, label: t('SCHEDULED_MESSAGES.WEEKDAYS.SATURDAY') },
  { value: 0, label: t('SCHEDULED_MESSAGES.WEEKDAYS.SUNDAY') },
];

const recurrenceOptions = [
  { value: 'none', label: t('SCHEDULED_MESSAGES.RECURRENCE.NONE') },
  { value: 'daily', label: t('SCHEDULED_MESSAGES.RECURRENCE.DAILY') },
  { value: 'weekly', label: t('SCHEDULED_MESSAGES.RECURRENCE.WEEKLY') },
  { value: 'monthly', label: t('SCHEDULED_MESSAGES.RECURRENCE.MONTHLY') },
];

const endOptions = [
  { value: 'never', label: t('SCHEDULED_MESSAGES.END.NEVER') },
  { value: 'on_date', label: t('SCHEDULED_MESSAGES.END.ON_DATE') },
  { value: 'after_count', label: t('SCHEDULED_MESSAGES.END.AFTER_COUNT') },
];

const statusLabels = {
  pending: t('SCHEDULED_MESSAGES.STATUS.PENDING'),
  paused: t('SCHEDULED_MESSAGES.STATUS.PAUSED'),
  completed: t('SCHEDULED_MESSAGES.STATUS.COMPLETED'),
  cancelled: t('SCHEDULED_MESSAGES.STATUS.CANCELLED'),
  failed: t('SCHEDULED_MESSAGES.STATUS.FAILED'),
};

const createInitialForm = () => ({
  content: props.initialContent || '',
  scheduledAt: '',
  recurrence: 'none',
  interval: 1,
  weekDays: [1],
  endType: 'never',
  endDate: '',
  endCount: 5,
  holdOnReply: false,
});

const form = reactive(createInitialForm());

const isRecurring = computed(() => form.recurrence !== 'none');
const isWeekly = computed(() => form.recurrence === 'weekly');
const isSubmitDisabled = computed(
  () => !form.content.trim() || !form.scheduledAt || isSaving.value
);

const dateInputValue = date =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

const resetForm = () => {
  Object.assign(form, createInitialForm());
  form.scheduledAt = dateInputValue(new Date(Date.now() + 60 * 60 * 1000));
};

const loadSchedules = async () => {
  isFetching.value = true;
  try {
    const { data } = await ScheduledMessagesAPI.get(props.conversationId);
    schedules.value = data;
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.API.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const recurrenceRule = () => {
  if (!isRecurring.value) return {};

  const rule = {
    frequency: form.recurrence,
    interval: Number(form.interval),
    end_type: form.endType,
  };

  if (isWeekly.value) rule.week_days = [...form.weekDays];
  if (form.endType === 'on_date') rule.end_date = form.endDate;
  if (form.endType === 'after_count') rule.end_count = Number(form.endCount);

  return rule;
};

const close = () => {
  emit('close');
  dialogRef.value?.close();
};

const open = async () => {
  resetForm();
  await nextTick();
  dialogRef.value?.open();
  await loadSchedules();
};

const createSchedule = async () => {
  if (isSubmitDisabled.value) return;

  isSaving.value = true;
  try {
    const { data } = await ScheduledMessagesAPI.create(props.conversationId, {
      scheduled_message: {
        content: form.content.trim(),
        scheduled_at: new Date(form.scheduledAt).toISOString(),
        hold_on_reply: form.holdOnReply,
        recurrence_rule: recurrenceRule(),
      },
    });
    schedules.value = [...schedules.value, data];
    useAlert(t('SCHEDULED_MESSAGES.API.CREATE_SUCCESS'));
    resetForm();
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.API.CREATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const runAction = async (schedule, action) => {
  pendingActionId.value = schedule.id;
  try {
    const response = await action();
    schedules.value = schedules.value.map(item =>
      item.id === schedule.id ? response.data : item
    );
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.API.ACTION_ERROR'));
  } finally {
    pendingActionId.value = null;
  }
};

const cancelSchedule = async schedule => {
  pendingActionId.value = schedule.id;
  try {
    await ScheduledMessagesAPI.delete(props.conversationId, schedule.id);
    schedules.value = schedules.value.filter(item => item.id !== schedule.id);
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.API.ACTION_ERROR'));
  } finally {
    pendingActionId.value = null;
  }
};

const formatDate = timestamp =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(timestamp * 1000));

const statusClass = status => {
  const classes = {
    pending: 'bg-n-blue-3 text-n-blue-11',
    paused: 'bg-n-amber-3 text-n-amber-11',
    completed: 'bg-n-green-3 text-n-green-11',
    cancelled: 'bg-n-slate-3 text-n-slate-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return classes[status] || classes.pending;
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('SCHEDULED_MESSAGES.TITLE')"
    :description="t('SCHEDULED_MESSAGES.DESCRIPTION')"
    :confirm-button-label="t('SCHEDULED_MESSAGES.CREATE')"
    :disable-confirm-button="isSubmitDisabled"
    :is-loading="isSaving"
    @confirm="createSchedule"
    @close="emit('close')"
  >
    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div class="flex flex-col gap-4">
        <TextArea
          v-model="form.content"
          auto-height
          :label="t('SCHEDULED_MESSAGES.FORM.CONTENT')"
          :placeholder="t('SCHEDULED_MESSAGES.FORM.CONTENT_PLACEHOLDER')"
        />
        <Input
          v-model="form.scheduledAt"
          type="datetime-local"
          :label="t('SCHEDULED_MESSAGES.FORM.SCHEDULED_AT')"
        />
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.FORM.RECURRENCE') }}
          </span>
          <Select v-model="form.recurrence" :options="recurrenceOptions" />
        </div>
        <Input
          v-if="isRecurring"
          v-model="form.interval"
          type="number"
          min="1"
          :label="t('SCHEDULED_MESSAGES.FORM.INTERVAL')"
        />
        <div v-if="isWeekly" class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.FORM.WEEK_DAYS') }}
          </span>
          <label
            v-for="day in weekDays"
            :key="day.value"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <Checkbox
              :model-value="form.weekDays.includes(day.value)"
              @change="
                event => {
                  if (event.target.checked) form.weekDays.push(day.value);
                  else
                    form.weekDays = form.weekDays.filter(
                      value => value !== day.value
                    );
                }
              "
            />
            {{ day.label }}
          </label>
        </div>
        <div v-if="isRecurring" class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.FORM.END') }}
          </span>
          <Select v-model="form.endType" :options="endOptions" />
        </div>
        <Input
          v-if="isRecurring && form.endType === 'on_date'"
          v-model="form.endDate"
          type="date"
          :label="t('SCHEDULED_MESSAGES.FORM.END_DATE')"
        />
        <Input
          v-if="isRecurring && form.endType === 'after_count'"
          v-model="form.endCount"
          type="number"
          min="1"
          :label="t('SCHEDULED_MESSAGES.FORM.END_COUNT')"
        />
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <Checkbox v-model="form.holdOnReply" />
          {{ t('SCHEDULED_MESSAGES.FORM.HOLD_ON_REPLY') }}
        </label>
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h4 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.LIST.TITLE') }}
          </h4>
          <span v-if="isFetching" class="text-xs text-n-slate-11">
            {{ t('SCHEDULED_MESSAGES.LIST.LOADING') }}
          </span>
        </div>
        <p
          v-if="!isFetching && !schedules.length"
          class="text-sm text-n-slate-11"
        >
          {{ t('SCHEDULED_MESSAGES.LIST.EMPTY') }}
        </p>
        <div
          v-for="schedule in schedules"
          :key="schedule.id"
          class="flex flex-col gap-3 rounded-xl border border-n-weak p-3"
        >
          <div class="flex items-start justify-between gap-3">
            <p class="mb-0 line-clamp-2 text-sm text-n-slate-12">
              {{ schedule.content }}
            </p>
            <span
              class="rounded-md px-1.5 py-0.5 text-xxs font-medium"
              :class="statusClass(schedule.status)"
            >
              {{ statusLabels[schedule.status] }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11">
            {{ formatDate(schedule.scheduled_at) }}
          </span>
          <div class="flex flex-wrap gap-2">
            <Button
              v-if="schedule.status === 'pending'"
              xs
              slate
              faded
              :label="t('SCHEDULED_MESSAGES.ACTIONS.PAUSE')"
              :disabled="pendingActionId === schedule.id"
              @click="
                runAction(schedule, () =>
                  ScheduledMessagesAPI.pause(conversationId, schedule.id)
                )
              "
            />
            <Button
              v-if="schedule.status === 'paused'"
              xs
              slate
              faded
              :label="t('SCHEDULED_MESSAGES.ACTIONS.RESUME')"
              :disabled="pendingActionId === schedule.id"
              @click="
                runAction(schedule, () =>
                  ScheduledMessagesAPI.resume(conversationId, schedule.id)
                )
              "
            />
            <Button
              v-if="
                schedule.status === 'pending' || schedule.status === 'paused'
              "
              xs
              slate
              faded
              :label="t('SCHEDULED_MESSAGES.ACTIONS.SEND_NOW')"
              :disabled="pendingActionId === schedule.id"
              @click="
                runAction(schedule, () =>
                  ScheduledMessagesAPI.sendNow(conversationId, schedule.id)
                )
              "
            />
            <Button
              v-if="
                schedule.status === 'pending' || schedule.status === 'paused'
              "
              xs
              ruby
              faded
              :label="t('SCHEDULED_MESSAGES.ACTIONS.CANCEL')"
              :disabled="pendingActionId === schedule.id"
              @click="cancelSchedule(schedule)"
            />
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
